import { useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  Check,
  ChevronDown,
  Circle,
  Loader2,
  Send,
  ArrowUpRight,
  Dumbbell,
  Utensils,
  FolderOpen,
  ClipboardCheck,
  UserPlus,
  type LucideIcon,
} from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { COACH_ONBOARDING } from '@/features/onboarding/coachOnboarding'
import { useCoachOnboardingProgress } from '@/lib/hooks/useCoachOnboardingProgress'
import { useCoach, useOrg } from '@/lib/states/session/hooks'
import { trpc } from '@/lib/trpc/client'
import { trpcVanilla } from '@/lib/trpc/vanilla'
import { cn } from '@/lib/utils/ui'

export const Route = createFileRoute('/_auth/_dashboard/getting-started')({
  component: GettingStartedPage,
})

function StepProgressRing({
  completed,
  total,
}: {
  completed: number
  total: number
}) {
  const value = total > 0 ? Math.max(0, Math.min(1, completed / total)) : 0
  const radius = 9
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference * (1 - value)

  return (
    <svg width="22" height="22" viewBox="0 0 22 22" className="shrink-0">
      <circle
        cx="11"
        cy="11"
        r={radius}
        fill="none"
        stroke="hsl(var(--border))"
        strokeWidth="2.5"
      />
      <circle
        cx="11"
        cy="11"
        r={radius}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        transform="rotate(-90 11 11)"
      />
    </svg>
  )
}

const STEP_TITLES: Record<string, string> = {
  experience_client: 'Experience Client App',
  setup_system: "Explore Trainrr's Powerful Features",
}

const STEP_DESCRIPTIONS: Record<string, string> = {
  experience_client:
    "Step into your client's shoes and experience the app as they will, through a personalized demo prepared just for you.",
  setup_system:
    'Advanced tools to save you time, streamline your workflow, and help you coach more effectively.',
}

const FEATURE_DETAILS: Record<
  string,
  { title: string; description: string; ctaLabel: string; ctaTo: string }
> = {
  'setup.program.create': {
    title: 'Build Reusable Programs',
    description:
      'Build client programs in minutes and reuse them across multiple clients.',
    ctaLabel: 'Explore now',
    ctaTo: '/training',
  },
  'setup.meals.create': {
    title: 'Create Meal Plans',
    description:
      'Generate personalized meal plans to provide structured nutrition guidance with ease.',
    ctaLabel: 'Open nutrition',
    ctaTo: '/nutrition',
  },
  'setup.resource.view': {
    title: 'Resource Collections',
    description:
      'Turn your knowledge into revenue. Share videos, PDFs, and guides that your clients can access anytime.',
    ctaLabel: 'Explore vault',
    ctaTo: '/vault',
  },
  'setup.checkins.manage': {
    title: 'Client Check-ins',
    description:
      'Manage weekly check-ins and review submissions to keep clients accountable.',
    ctaLabel: 'Open check-ins',
    ctaTo: '/checkins',
  },
  'setup.leads.forms': {
    title: 'Lead Forms',
    description:
      'Capture inbound leads with public forms and turn interest into onboarding conversations.',
    ctaLabel: 'Open leads',
    ctaTo: '/leads',
  },
}

const SETUP_ITEM_ICONS: Record<string, LucideIcon> = {
  'setup.program.create': Dumbbell,
  'setup.meals.create': Utensils,
  'setup.resource.view': FolderOpen,
  'setup.checkins.manage': ClipboardCheck,
  'setup.leads.forms': UserPlus,
}

function GettingStartedPage() {
  const coach = useCoach()
  const org = useOrg()
  const { itemStatesLoaded, completedItemKeys, completeItem, steps } =
    useCoachOnboardingProgress()

  const [openStepKey, setOpenStepKey] = useState<string | null | undefined>(
    undefined,
  )
  const [selectedSetupItemKey, setSelectedSetupItemKey] = useState(
    COACH_ONBOARDING.steps.find((step) => step.key === 'setup_system')?.items[0]
      ?.key ?? 'setup.program.create',
  )
  const [sendingInvite, setSendingInvite] = useState(false)
  const [inviteTriggered, setInviteTriggered] = useState(false)

  const { data: demoClient } = trpc.clients.demo.useQuery(undefined, {
    enabled: Boolean(org?.id),
  })

  const completedSet = useMemo(
    () => new Set(completedItemKeys),
    [completedItemKeys],
  )
  const demoLoginCompleted = completedSet.has('experience.demo.login')
  const demoInviteSent =
    completedSet.has('experience.demo.invite') || inviteTriggered
  const selectedSetupFeature =
    FEATURE_DETAILS[selectedSetupItemKey] ??
    FEATURE_DETAILS['setup.program.create']
  const defaultOpenStepKey = demoLoginCompleted
    ? 'setup_system'
    : 'experience_client'
  const resolvedOpenStepKey =
    openStepKey === undefined ? defaultOpenStepKey : openStepKey

  const onInviteMyself = async () => {
    if (!demoClient?.id) {
      toast.error('Demo client not found', {
        description: 'Create or refresh your demo client and try again.',
      })
      return
    }

    try {
      setSendingInvite(true)
      await trpcVanilla.clients.sendInviteEmail.mutate({
        clientId: demoClient.id,
      })

      toast.success('Invite sent', {
        description: 'Check your inbox for app download instructions.',
      })
      setInviteTriggered(true)
      await completeItem('experience.demo.invite', 'manual')
    } catch (error: any) {
      toast.error('Failed to send invite', {
        description: error?.message,
      })
    } finally {
      setSendingInvite(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 md:px-8 md:py-10">
      <header className="mb-6 text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground md:text-2xl">
          <span className="mr-2">👋</span>
          Welcome to Trainrr{coach?.firstName ? `, ${coach.firstName}` : ''}!
        </h1>
        <p className="mt-2 text-xs text-muted-foreground md:text-sm">
          Explore our powerful tools to help you save time and grow your
          training business.
        </p>
      </header>

      <div className="space-y-4">
        {!itemStatesLoaded ? (
          <div className="rounded-3xl border bg-card p-5 text-sm text-muted-foreground">
            Loading getting started...
          </div>
        ) : (
          COACH_ONBOARDING.steps.slice(0, 2).map((step, index) => {
            const stepProgress = steps.find((s) => s.key === step.key)
            const stepCompleted =
              step.key === 'experience_client'
                ? demoLoginCompleted
                  ? 1
                  : 0
                : (stepProgress?.completed ?? 0)
            const stepTotal =
              step.key === 'experience_client'
                ? 1
                : (stepProgress?.total ?? step.items.length)
            const isOpen = resolvedOpenStepKey === step.key

            return (
              <Collapsible
                key={step.key}
                open={isOpen}
                onOpenChange={(open) => {
                  setOpenStepKey(open ? step.key : null)
                }}
                className="overflow-hidden rounded-3xl border bg-card shadow-sm"
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left md:px-7"
                  >
                    <div className="flex min-w-0 items-center gap-4">
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold text-primary">
                        {index + 1}
                      </span>
                      <span className="truncate text-base font-semibold text-foreground md:text-lg">
                        {STEP_TITLES[step.key] ?? step.title}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-4 text-muted-foreground">
                      <span className="inline-flex items-center gap-2 text-xs md:text-sm">
                        <StepProgressRing
                          completed={stepCompleted}
                          total={stepTotal}
                        />
                        {stepCompleted}/{stepTotal} explored
                      </span>
                      <ChevronDown
                        className={cn(
                          'size-6 transition-transform',
                          isOpen && 'rotate-180',
                        )}
                      />
                    </div>
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-[collapsible-up_220ms_ease-in] data-[state=open]:animate-[collapsible-down_280ms_ease-out]">
                  <div className="border-t px-5 pb-6 pt-5 md:px-7 md:pb-7">
                    <p className="mb-5 text-center text-base text-muted-foreground">
                      {STEP_DESCRIPTIONS[step.key]}
                    </p>

                    {step.key === 'experience_client' && (
                      <div className="space-y-5">
                        <img
                          src="https://iacablenmfsxqxikdrlc.supabase.co/storage/v1/object/public/public-images/client-app.webp"
                          alt="Client app preview"
                          className="mx-auto h-36 w-full max-w-3xl rounded-2xl object-cover shadow-sm"
                        />
                        {demoInviteSent && (
                          <p className="text-center text-sm text-muted-foreground">
                            Email has been sent. Please log in to start
                            exploring the Client App. If you don&apos;t see it,
                            click{' '}
                            <button
                              type="button"
                              onClick={onInviteMyself}
                              className="font-medium text-primary underline underline-offset-4"
                              disabled={sendingInvite}
                            >
                              Resend
                            </button>
                            .
                          </p>
                        )}
                        <div className="flex justify-center">
                          <Button
                            className="h-10 rounded-xl px-6 text-sm"
                            onClick={onInviteMyself}
                            disabled={sendingInvite}
                          >
                            {sendingInvite ? (
                              <Loader2 className="mr-2 size-4 animate-spin" />
                            ) : (
                              <Send className="mr-2 size-4" />
                            )}
                            {demoInviteSent ? 'Resend invite' : 'Invite myself'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {step.key === 'setup_system' && (
                      <div className="grid gap-5 md:grid-cols-[1fr_1.1fr]">
                        <div className="space-y-2 rounded-2xl bg-muted/40 p-3">
                          {step.items.map((item) => {
                            const done = completedSet.has(item.key)
                            const selected = selectedSetupItemKey === item.key
                            const ItemIcon = SETUP_ITEM_ICONS[item.key]
                            return (
                              <button
                                key={item.key}
                                type="button"
                                onClick={() =>
                                  setSelectedSetupItemKey(item.key)
                                }
                                className={cn(
                                  'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm md:text-base',
                                  selected && 'bg-background shadow-sm',
                                  done && 'text-muted-foreground',
                                  !done && 'hover:bg-background',
                                )}
                              >
                                {done ? (
                                  <span className="inline-flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                    <Check className="size-4" />
                                  </span>
                                ) : (
                                  <Circle className="size-5 shrink-0 text-muted-foreground" />
                                )}
                                {ItemIcon && (
                                  <ItemIcon
                                    className={cn(
                                      'size-4 shrink-0',
                                      done
                                        ? 'text-muted-foreground'
                                        : 'text-foreground',
                                    )}
                                  />
                                )}
                                <span className="font-medium leading-tight">
                                  {item.title}
                                </span>
                              </button>
                            )
                          })}
                        </div>

                        <div className="rounded-2xl p-4">
                          <h3 className="text-xl font-semibold text-foreground md:text-2xl">
                            {selectedSetupFeature.title}
                          </h3>
                          <p className="mt-2 text-sm text-muted-foreground md:text-base">
                            {selectedSetupFeature.description}
                          </p>
                          <Button
                            asChild
                            className="mt-4 h-9 rounded-xl px-5 text-sm"
                          >
                            <Link to={selectedSetupFeature.ctaTo}>
                              {selectedSetupFeature.ctaLabel}
                              <ArrowUpRight className="size-4" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )
          })
        )}
      </div>
    </div>
  )
}
